import { Component, Input, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CustomerInfo } from '../../order-v2.types';
import { ClipboardService } from '../../../../../core/services/clipboard/clipboard.service';

interface CustomerField {
  key: string;
  label: string;
  value: string | null | undefined;
  emphasis?: 'mono' | 'break-all';
}

@Component({
  selector: 'app-customer-card',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm dark:border-neutral-800 dark:bg-neutral-800/40">
      <div class="overflow-hidden">
        <div class="mb-2.5 flex items-center gap-2 text-lg font-semibold text-neutral-900 dark:text-white">
          <span>cliente</span>
          <span
            class="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium"
            [ngClass]="customer.type === 'Corporate'
              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'
              : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'"
          >
            {{ customer.type === 'Corporate' ? 'Pessoa Jurídica' : 'Pessoa Física' }}
          </span>
        </div>

        @for (field of fields(); track field.key) {
          <div class="flex items-start gap-1.5 py-0.5 text-sm text-neutral-900 dark:text-neutral-100">
            <button
              type="button"
              (click)="copyValue(field.value)"
              [title]="'Copiar ' + field.label"
              class="flex min-w-[80px] shrink-0 cursor-pointer items-center bg-transparent p-0 text-left text-xs text-neutral-500 focus-visible:outline-none dark:text-neutral-400"
            >
              <span class="truncate">{{ field.label }}</span>
            </button>
            <span
              class="min-w-0 flex-1"
              [ngClass]="field.emphasis === 'break-all' ? 'break-all' : 'break-words'"
              [title]="field.emphasis === 'break-all' ? field.value : null"
            >
              {{ field.value }}
            </span>
          </div>
        }
      </div>
    </div>
  `,
})
export class CustomerCardComponent {
  @Input({ required: true }) customer!: CustomerInfo;

  private readonly _clipboard = inject(ClipboardService);

  readonly fields = computed<CustomerField[]>(() => {
    const c = this.customer;
    if (!c) return [];
    const isCorporate = c.type === 'Corporate';
    const items: CustomerField[] = [];

    if (isCorporate && c.corporateName) {
      items.push({ key: 'corporateName', label: 'razão social', value: c.corporateName });
    }
    if (isCorporate && c.tradeName) {
      items.push({ key: 'tradeName', label: 'nome fantasia', value: c.tradeName });
    }
    items.push({ key: 'name', label: isCorporate ? 'contato' : 'nome', value: c.name });
    items.push({ key: 'document', label: c.documentType.toLowerCase(), value: c.documentNumber });
    items.push({ key: 'email', label: 'e-mail', value: c.email, emphasis: 'break-all' });
    if (c.phone) {
      items.push({ key: 'phone', label: 'telefone', value: c.phone });
    }

    return items.filter((f) => f.value !== null && f.value !== undefined && String(f.value).trim().length > 0);
  });

  copyValue(value: string | null | undefined): void {
    if (value === null || value === undefined) return;
    this._clipboard.copy(String(value));
  }
}
